import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form943DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 943 Due Dates: Filing, Depositing & Penalties Guide"
          description="Form 943 Due Dates: Learn about filing deadlines, where to file, tax deposits, and penalties. Ensure timely compliance for 2025 with our comprehensive guide"
          keywords=""
          canonical="resources/employment-tax/form-943-due-dates/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 943 </span>
          </span>{' '}
          Due Dates
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-943-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 943 Due Date at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-943-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          <b>Form 943</b>
                        </Link>
                        {
                          ' is an annual federal tax return used by agricultural employers to report '
                        }
                        <b>
                          federal income tax withheld and Social Security and
                          Medicare taxes
                        </b>
                        {' from farm employees’ wages.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File:</b>
                        {' Employers who hire and pay '}
                        <b>agricultural (farm) employees</b>
                        {
                          ' generally use Form 943 to report employment taxes for those workers.'
                        }
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Filing Frequency:</b>
                        {' Form 943 is filed '}
                        <b>annually</b>
                        {', rather than quarterly like '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 941
                        </Link>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Filing Deadline:</b>
                        {' For the '}
                        <b>2025 tax year</b>
                        {', Form 943 is due '}
                        <b>February 2, 2026</b>
                        {
                          '. If all required taxes were deposited on time and in full, employers may generally file by '
                        }
                        <b>February 10, 2026</b>.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <b>Tax Deposits:</b>
                        {' If total taxes reported on Form 943 are '}
                        <b>less than $2,500</b>
                        {
                          ', the employer can generally pay the tax with the timely filed return. If the amount is '
                        }
                        <b>$2,500 or more</b>, deposits must be made
                        electronically according to the applicable deposit
                        schedule.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        <b>$100,000 Next-Day Deposit Rule:</b>
                        {' If an employer accumulates '}
                        <b>$100,000 or more in tax liability on any day</b>, the
                        employer becomes a semiweekly depositor starting the
                        next day.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="7" />
                      <span itemProp="name">
                        <b>Filing Methods:</b>
                        {' Employers can file Form 943 '}
                        <b>electronically or by paper</b>. The IRS recommends
                        electronic filing for faster and more convenient
                        processing.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When to File Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to File Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#taxes" sx={{ color: '#0202ff' }}>
                          Depositing Your Taxes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#interest"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          Penalties and Interest?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 943?
                </Typography>

                <Typography variant="body1" id="when" sx={{ mb: 3 }}>
                  <Link
                    href="/resources/employment-tax/form-943-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 943
                  </Link>
                  , Employer&apos;s Annual Federal Tax Return for Farm
                  Employees, is used to report how federal income tax, Social
                  Security, or Medicare tax withheld from employees’ wages. This
                  form must be filed by employer who hire and pay agricultural
                  workers.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When to File Form 943?
                </Typography>
                <Typography variant="body1" id="where" sx={{ mb: 3 }}>
                  For the tax year 2025, Form 943 is due by February 02, 2026.
                  If you have deposited all your taxes on time and in full, you
                  may file by February 10, 2026. Failure to file by the due date
                  may result in a penalty of 5% of the unpaid tax for each month
                  or part of a month the return is late, up to a maximum of 25%.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to File Form 943?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Electronic Filing:
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    The IRS recommends filing Form 943 electronically. TaxZerone
                    is an affordable and straightforward e-filing service
                    provider that allows you to file within a few minutes.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Paper Filing:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    If filing by paper, the mailing address depends on whether
                    you include a payment with your return. Refer to the table
                    below for the correct address based on your location.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Mailing Address:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Location
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Without a Payment . . .
                          </TableCell>
                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            With a Payment . . .
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Georgia, Illinois, Indiana, Kentucky, Maine,
                            Maryland, Massachusetts, Michigan, New Hampshire,
                            New Jersey, New York, North Carolina, Ohio,
                            Pennsylvania, Rhode Island, South Carolina,
                            Tennessee, Vermont, Virginia, West Virginia,
                            Wisconsin{' '}
                          </TableCell>

                          <TableCell>
                            Department of the Treasury
                            <br /> Internal Revenue Service
                            <br /> Kansas City, MO
                            <br /> 64999-0008
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br /> P.O. Box 932200
                            <br />
                            Louisville, KY 40293-2200
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alabama, Alaska, Arizona, Arkansas, California,
                            Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                            Louisiana, Minnesota, Mississippi, Missouri,
                            Montana, Nebraska, Nevada, New Mexico, North Dakota,
                            Oklahoma, Oregon, South Dakota, Texas, Utah,
                            Washington, Wyoming
                          </TableCell>

                          <TableCell>
                            Department of the Treasury
                            <br /> Internal Revenue Service
                            <br /> Ogden, UT 84201-0008
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service <br />
                            P.O. Box 932200 <br />
                            Louisville, KY 40293-2200
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            No legal residence or principal place of business in
                            any state, include employers in Puerto Rico
                          </TableCell>

                          <TableCell>
                            Internal Revenue Service
                            <br /> P.O. Box 409101 Ogden,
                            <br /> UT 84409
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service <br />
                            P.O. Box 932200 <br />
                            Louisville, KY 40293-2200
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Special filing address for exempt organizations;
                            federal, state, and local governmental entities; and
                            Indian tribal governmental entities, regardless of
                            location{' '}
                          </TableCell>

                          <TableCell id="isthere">
                            Department of the Treasury
                            <br /> Internal Revenue Service
                            <br /> Ogden, UT 84201-0008{' '}
                          </TableCell>
                          <TableCell id="isthere">
                            Internal Revenue Service <br />
                            P.O. Box 932200 <br />
                            Louisville, KY 40293-2200
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Using Private Delivery Services (PDS):
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Certain private delivery services (PDS) are authorized by the
                  IRS to deliver tax returns and payments. These services cannot
                  deliver to P.O. boxes, so use the U.S. Postal Service for P.O.
                  box addresses. Approved PDSs include DHL Express, Federal
                  Express (FedEx), and United Parcel Service (UPS).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For the most up-to-date information and a list of approved
                  PDSs and their addresses, visit:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <Link
                      href="https://www.irs.gov/filing/private-delivery-services-pds"
                      target="_blank"
                      id="taxes"
                      sx={{ color: '#2020ff' }}
                    >
                      IRS gov site{' '}
                    </Link>
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <Link
                      href="https://www.irs.gov/filing/submission-processing-center-street-addresses-for-private-delivery-service-pds"
                      target="_blank"
                      sx={{ color: '#2020ff' }}
                    >
                      IRS Processing Center
                    </Link>
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Depositing Your Taxes
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Electronic Funds Transfer (EFT):
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    All depository taxes must be deposited electronically via
                    EFT.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  When to Deposit Taxes:
                </Typography>
                <ol style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>If Total Taxes (Line 13) Are Less Than $2,500:</b>
                  </li>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ marginBottom: 12 }}>
                      Pay the tax due with your timely filed return
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      No need to make deposits throughout the year.
                    </li>
                  </ul>
                  <li style={{ marginBottom: 12 }}>
                    <b>If Total Taxes (Line 13) Are $2,500 or More:</b>
                  </li>
                  <ul style={{ marginBottom: 20 }}>
                    <li style={{ marginBottom: 12 }}>
                      Deposits must be made by EFT according to your deposit
                      schedule (monthly or semiweekly).
                    </li>
                    <li style={{ marginBottom: 12 }}>
                      Determine your deposit schedule at the beginning of each
                      year.
                    </li>
                  </ul>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Reducing Deposits for Sick and Family Leave Credits:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    You can reduce your deposits by the amount of anticipated
                    credits for qualified sick and family leave wages.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    There is no penalty for reducing deposits if conditions are
                    met (see instructions for lines 12b and 12d).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Important Rules:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> $100,000 Next-Day Deposit Rule:</b>
                  </li>
                  <ul style={{ listStyleType: 'disc' }}>
                    <li style={{ marginBottom: 12 }}>
                      If you accumulate $100,000 in tax liability on any day,
                      you become a semiweekly depositor the next day and remain
                      so for at least the rest of the calendar year and the
                      following year.
                    </li>
                    <li style={{ marginBottom: 12 }} id="interest">
                      The $100,000 threshold is before considering any reduction
                      for nonrefundable credits.
                    </li>
                  </ul>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties and Interest?
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    There is no extension for{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-943/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      filing Form 943
                    </Link>
                    , so file it on time to avoid a penalty.{' '}
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The IRS charges two penalties: failure to file and failure
                    to pay the appropriate amount.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Penalties range from 2% to 15%, depending on how late the
                    deposits are made.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If an employer can prove reasonable cause for failing to file
                  or make timely deposits, they can request a penalty abatement
                  from the IRS.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 940 within a few minutes using TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Choose a simplified tax filing process. Choose TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940duedates`}
              >
                E-file Form 940
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default Form943DueDates;
